import { usePermissions } from '@hooks/usePermissions';
import { LoadingPage } from '@partful/features/instructions/ui/LoadingPage';
import { useProductLibraryQuery } from '@partful/pages/product-library/hooks/useProductLibraryQuery';
import { Autocomplete } from '@partful/shared/ui/Autocomplete';
import { PageBrandedPanel } from '@partful/shared/ui/PageBrandedPanel';
import { PageNavigation } from '@partful/shared/ui/PageNavigation';
import { StandardPageHeader, StandardPageLayout, StandardPageMain } from '@partful/shared/ui/StandardPageLayout';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductContextProvider } from 'src/v2/shared/lib/providers/ProductContextProvider';
import styles from './InstructionsPage.module.scss';
import { categoriseCADJobByStatus, categoriseProductsByStatus } from './lib/productStatusHelpers';
import { InstructionCard } from './ui/InstructionCard';
import { NewInstructionCard } from './ui/NewInstructionCard';

export function InstructionsPage() {
  const { t: i18n } = useTranslation();
  const { enabledFeatures } = useTenant();
  const showNewProductCard = usePermissions(['add', 'product']) && enabledFeatures.includes('configuration-management');
  const showIncompleteProducts = usePermissions(['update', 'product']);

  const [searchTerm, setSearchTerm] = useState('');

  const jobType = 'CAD';
  const { isLoading, configurations, jobs: cadJobs } = useProductLibraryQuery(jobType);

  const {
    publishedProducts,
    draftProducts: configDraftProducts,
    otherProducts,
  } = useMemo(() => categoriseProductsByStatus(configurations ?? []), [configurations]);

  const {
    inProgressProducts,
    processingProducts,
    draftProducts: cadDraftProducts,
    failedProducts,
  } = useMemo(() => categoriseCADJobByStatus(cadJobs), [cadJobs]);

  const draftProducts = [...configDraftProducts, ...cadDraftProducts];

  const productsToShow = [...publishedProducts, ...draftProducts]
    .concat(
      showIncompleteProducts ? [...failedProducts, ...inProgressProducts, ...processingProducts, ...otherProducts] : []
    )
    .filter((product) =>
      searchTerm ? product.productExternalID?.toLowerCase().includes(searchTerm?.toLowerCase()) : true
    );

  const showLoader = useMemo(() => isLoading && productsToShow.length === 0, [isLoading, productsToShow]);

  return (
    <StandardPageLayout className={styles.page}>
      <StandardPageHeader>
        <PageBrandedPanel />
        <PageNavigation />
      </StandardPageHeader>
      <StandardPageMain>
        {showLoader && <LoadingPage />}
        {!showLoader && (
          <div className={styles.library}>
            <div className={styles.title}>
              <Typography appearance="heading1" as="h1">
                {i18n('instructions:library.title')}
              </Typography>
              <Autocomplete
                size="small"
                className={styles.searchBox}
                options={[...new Set(productsToShow.map((p) => p.productExternalID))]}
                placeholder={i18n('instructions:library.searchPlaceholder')}
                aria-label={i18n('instructions:library.search')}
                value={searchTerm}
                freeSolo
                onInput={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
                onChange={/* istanbul ignore next */ (_, value) => setSearchTerm(value as string)}
              />
            </div>
            <div className={styles.list}>
              {showNewProductCard && <NewInstructionCard data-testid="new-instruction-card" />}
              {productsToShow.map((product) => (
                <ProductContextProvider key={`${product.runID}-${product.productExternalID}`} name={product.name}>
                  <InstructionCard product={product} data-testid={product.productExternalID} />
                </ProductContextProvider>
              ))}
            </div>
          </div>
        )}
      </StandardPageMain>
    </StandardPageLayout>
  );
}
